import { AsYouType } from 'libphonenumber-js/min'
import replace from 'lodash/replace'
import startsWith from 'lodash/startsWith'
import sumBy from 'lodash/sumBy'
import React from 'react'
import Input from 'react-phone-number-input/input'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import { hasText } from 'utils/helpers'

import { QuestionTitle } from './Text'

export function buildPhoneNumberValue(i: any) {
  if (!hasText(i)) return undefined
  if (i.length === 10 && i[0] !== '1') return `+1${i}`
  if (i[0] === '+') return i

  return `+${i}`
}

function calculateMaxLength(currentValue) {
  const initialCount = startsWith(currentValue, '1 ') ? 0 : -1
  const notNumberCount = sumBy(currentValue, (char) =>
    char !== ' ' && Number.isInteger(+char) ? 0 : 1
  )
  return initialCount + 11 + notNumberCount
}

const CustomInput = React.forwardRef(
  (
    {
      id,
      className,
      onChange,
      onFocus,
      onBlur,
      disabled,
      trueValue,
      placeholder,
    },
    forwardedRef
  ) => {
    const handleChange = React.useCallback(
      (e) => {
        if (disabled) return
        const replaced = replace(e.target.value, /\D/g, '')
        return onChange(replaced)
      },
      [disabled, onChange]
    )

    return (
      <input
        autoComplete="tel"
        className={className}
        disabled={disabled}
        id={id}
        onBlur={onBlur}
        onChange={handleChange}
        onFocus={onFocus}
        placeholder={placeholder}
        ref={forwardedRef}
        type="tel"
        value={new AsYouType('US').input(replace(trueValue, /\+1|\D/g, ''))}
      />
    )
  }
)

/**
 * @deprecated Use `PhoneFieldAlt` instead
 */

function SingleLinePhone({
  className,
  disabled,
  errorLabel,
  grayOutWhenDisabled,
  hasError,
  isOfficeUse,
  isRequired,
  label,
  maxLength,
  inputId,
  name,
  onBlur,
  onChange,
  placeholder,
  style,
  value,
  willShowNotAllowedCursor,
}) {
  const inputRef = React.useRef()

  const inputValue = buildPhoneNumberValue(value)

  const maxLengthValue =
    maxLength ?? calculateMaxLength(inputRef?.current?.value)

  let classes = `form-input border focus:!border-primary focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)] rounded-md block w-full transition duration-150 ease-in-out text-gray-700 sm:text-sm sm:leading-5 ${className}`
  if (willShowNotAllowedCursor) {
    classes += ' cursor-not-allowed bg-gray-100'
  }

  if (hasError) {
    classes += ' border-red-500'
  }

  if (disabled && grayOutWhenDisabled) {
    classes += ' bg-gray-100'
  }

  const handleChange = (input) => {
    if (disabled) return
    if ((input?.length ?? 0) > 12) {
      const sliced = input?.slice?.(0, 12)
      return onChange(sliced)
    }
    return onChange(input)
  }

  return (
    <>
      {label && (
        <QuestionTitle
          htmlFor={name}
          isOfficeUse={isOfficeUse}
          isRequired={isRequired}
        >
          {label}
        </QuestionTitle>
      )}
      <Input
        className={classes}
        country="US"
        disabled={disabled}
        id={inputId || name}
        inputComponent={CustomInput}
        maxLengthValue={maxLengthValue}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        placeholder={placeholder}
        ref={inputRef}
        style={style}
        trueValue={value}
        value={inputValue}
      />
      {hasError && <ErrorLabel>{errorLabel}</ErrorLabel>}
    </>
  )
}

export default SingleLinePhone
